import { SITE_NAME } from './site';
import { WHATSAPP_NUMBER } from './contact';

export const SITE_URL = 'https://eskisehirdavetiye.com';

export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/products/davetiyeler/dugun/klasik-zarif.jpg`;

export function absoluteUrl(path = '/') {
  if (!path || path === '/') return `${SITE_URL}/`;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${normalized}`;
}

export function absoluteImageUrl(src) {
  if (!src) return DEFAULT_OG_IMAGE;
  if (src.startsWith('http://') || src.startsWith('https://')) return src;
  const path = src.startsWith('/') ? src : `/${src}`;
  return `${SITE_URL}${path}`;
}

function phoneE164() {
  if (!WHATSAPP_NUMBER || WHATSAPP_NUMBER.length < 10) return undefined;
  return `+${WHATSAPP_NUMBER}`;
}

export const pageMeta = {
  home: {
    title: `${SITE_NAME} | Davetiye modelleri, sade ve lüks seçenekler`,
    description:
      'Eskişehir davetiye: ekonomik davetiye, sade davetiye ve lüks davetiye modelleri. Düğün, nişan, sünnet davetiye çeşitleri ve şekerlik.',
    path: '/',
  },
  davetiyeler: {
    title: `Davetiye çeşitleri | ${SITE_NAME}`,
    description:
      'Eskişehir davetiye çeşitleri ve davetiye modelleri: ekonomik, sade ve lüks seçenekler. Düğün, nişan ve sünnet koleksiyonları.',
    path: '/davetiyeler',
  },
  sekerlik: {
    title: `Şekerlik modelleri | ${SITE_NAME}`,
    description:
      'Eskişehir şekerlik modelleri: düğün, nişan, sünnet ve doğum için davetiyenizle uyumlu ikram ambalajları.',
    path: '/sekerlik',
  },
  about: {
    title: `Hakkımızda | ${SITE_NAME}`,
    description:
      'Eskişehir’de davetiye ve şekerlik: ekonomik, sade ve lüks davetiye modelleri; tasarımdan baskıya yerel üretim.',
    path: '/hakkimizda',
  },
  notFound: {
    title: `Sayfa bulunamadı | ${SITE_NAME}`,
    description: 'Aradığınız sayfa bulunamadı.',
    path: undefined,
    robots: 'noindex, follow',
  },
};

const categoryMetaMap = {
  davetiyeler: {
    dugun: {
      title: `Eskişehir düğün davetiyesi | ekonomik ve lüks modeller`,
      description:
        'Eskişehir düğün davetiyesi modelleri: sade davetiye, ekonomik davetiye ve lüks davetiye seçenekleri. Özel baskı ve hızlı iletişim.',
    },
    sunnet: {
      title: `Eskişehir sünnet davetiyesi | davetiye modelleri`,
      description:
        'Eskişehir sünnet davetiyesi çeşitleri: sade, ekonomik ve şık davetiye modelleri. Çocuk dostu motifler ve kaliteli baskı.',
    },
    nisan: {
      title: `Eskişehir nişan davetiyesi | sade ve lüks seçenekler`,
      description:
        'Eskişehir nişan davetiyesi modelleri: sade davetiye, ekonomik ve lüks davetiye çeşitleri. Butik üretim ve özel tasarım.',
    },
  },
  sekerlik: {
    dugun: {
      title: `Eskişehir düğün şekerliği | şekerlik modelleri`,
      description:
        'Eskişehir düğün şekerliği modelleri: kutu, kese ve etiket uyumlu setler. Davetiyenizle bütünleşen ikram ambalajları.',
    },
    nisan: {
      title: `Eskişehir nişan şekerliği | şekerlik modelleri`,
      description:
        'Eskişehir nişan şekerliği: nişan masanıza uygun şık şekerlik modelleri ve ambalaj seçenekleri.',
    },
    sunnet: {
      title: `Eskişehir sünnet şekerliği | şekerlik modelleri`,
      description:
        'Eskişehir sünnet şekerliği modelleri: kişiselleştirilebilir, renkli ve davetiyenizle uyumlu ikram ambalajları.',
    },
    dogum: {
      title: `Eskişehir doğum şekerliği | şekerlik modelleri`,
      description:
        'Eskişehir doğum şekerliği: bebek karşılama ve kutlamalar için tatlı şekerlik modelleri.',
    },
  },
};

export function getCategoryMeta(hub, slug, category) {
  const preset = categoryMetaMap[hub]?.[slug];
  if (preset) return preset;
  const label = category?.title || slug;
  return {
    title: `${label} | ${SITE_NAME}`,
    description:
      category?.description ||
      `${label} modelleri — ${SITE_NAME}. Ekonomik, sade ve lüks seçenekler.`,
  };
}

export function getProductMeta(product, category) {
  const catLabel = category?.title || 'Davetiye';
  return {
    title: `${product.name} | ${SITE_NAME}`,
    description:
      product.hint?.trim() ||
      `${product.name} — ${catLabel}. Eskişehir’de ekonomik, sade ve lüks davetiye modelleri.`,
  };
}

export function buildLocalBusinessJsonLd() {
  const telephone = phoneE164();
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: SITE_NAME,
    url: SITE_URL,
    image: DEFAULT_OG_IMAGE,
    description:
      'Eskişehir’de davetiye modelleri ve çeşitleri: ekonomik davetiye, sade davetiye, lüks davetiye; şekerlik ve dijital davetiye.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Eskişehir',
      addressCountry: 'TR',
    },
    areaServed: {
      '@type': 'City',
      name: 'Eskişehir',
    },
    ...(telephone ? { telephone } : {}),
  };
}

export function buildBreadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function buildItemListJsonLd(name, items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: absoluteUrl(item.path),
    })),
  };
}

export function buildProductJsonLd(product, category, path) {
  const image = absoluteImageUrl(product.image);
  const offers =
    !product.priceOnRequest &&
    product.price != null &&
    product.price !== '' &&
    !Number.isNaN(Number(product.price))
      ? {
          '@type': 'Offer',
          priceCurrency: 'TRY',
          price: String(Number(product.price)),
          availability: 'https://schema.org/InStock',
          url: absoluteUrl(path),
        }
      : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description:
      product.hint?.trim() ||
      `${product.name} — ${category?.title || 'Davetiye'} | ${SITE_NAME}`,
    image,
    url: absoluteUrl(path),
    brand: {
      '@type': 'Brand',
      name: SITE_NAME,
    },
    ...(category?.title
      ? { category: category.title }
      : {}),
    ...(offers ? { offers } : {}),
  };
}
